"use client";

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CalculatorBuyBridgeData } from "@/lib/calculator/buy-bridge-data";

/**
 * Client-side channel between the Suspense-quarantined buy-bridge server loader and the
 * SINGLE stable calculator client instance.
 *
 * Two constraints collide on the calculator routes (ISR + Cache Components):
 *
 * 1. The interactive calculator must mount exactly ONCE, outside any Suspense boundary —
 *    a fallback→resolved swap remounts the client and discards user input (the UX bug
 *    51c50811c fixed).
 * 2. The buy-bridge data needs request-time APIs (headers + geo-pref cookie + FX via the
 *    pricing resolver), which must only run inside a Suspense-quarantined async SERVER
 *    child (`await connection()` first). Starting that work as a detached promise in the
 *    prerenderable shell scope attributes the request-API access to the ISR shell —
 *    `DYNAMIC_SERVER_USAGE` + `Invalid revalidate configuration provided: 0 < 1`, and
 *    background ISR regeneration fails.
 *
 * Resolution: the server wrapper renders BOTH as siblings under this provider — the bare
 * calculator client (stable mount, prerenderable) and the quarantined loader, whose
 * streamed `<BuyBridgeDataBridge>` injects the resolved data into this context. The
 * calculator consumes it via `useBuyBridgeData()`; no request-bound promise ever exists
 * in shell scope.
 */

type BuyBridgeDataContextValue = {
	data: CalculatorBuyBridgeData | null;
	setData: (data: CalculatorBuyBridgeData) => void;
};

const BuyBridgeDataContext = createContext<BuyBridgeDataContextValue | null>(null);

export function BuyBridgeDataProvider({ children }: { children: ReactNode }) {
	const [data, setData] = useState<CalculatorBuyBridgeData | null>(null);
	const value = useMemo<BuyBridgeDataContextValue>(() => ({ data, setData }), [data]);
	return <BuyBridgeDataContext.Provider value={value}>{children}</BuyBridgeDataContext.Provider>;
}

/**
 * `null` ⇒ the server loader has not streamed / injected yet — callers render the
 * loading skeleton (the same visual the former Suspense fallback showed).
 */
export function useBuyBridgeData(): CalculatorBuyBridgeData | null {
	return useContext(BuyBridgeDataContext)?.data ?? null;
}

/**
 * Rendered by the Suspense-quarantined server loader once the buy-bridge data resolved.
 * Feeds the data into the provider on mount and renders nothing itself. `data` arrives as
 * a server-serialized prop with a stable reference, so the effect fires once per stream.
 */
export function BuyBridgeDataBridge({ data }: { data: CalculatorBuyBridgeData }) {
	const setData = useContext(BuyBridgeDataContext)?.setData;
	useEffect(() => {
		setData?.(data);
	}, [setData, data]);
	return null;
}
